import styled from 'styled-components';

import { useMutation } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import Classification, { ClassificationApiName } from 'models/Classification';
import { classificationsQueryKeys } from 'services/api/classification';
import { clientClassificationsQueryKeys, deleteClientClassification } from 'services/api/clientClassifications';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

type RemoveConfirmModalProps = {
  healthPlan: Classification;
  handleClose: () => void;
  clientId: string;
};

const RemoveConfirmModal = ({ healthPlan, handleClose, clientId }: RemoveConfirmModalProps) => {
  const { addToast } = useToastActions();
  const queryClient = useQueryClient();

  const { mutate: deletePlan } = useMutation({
    mutationFn: () =>
      deleteClientClassification({
        classificationType: ClassificationApiName.HEALTH_PLAN,
        classificationId: healthPlan.id,
      }),
    onSuccess: () => {
      addToast({
        text: `${healthPlan.name} was successfully removed as a contracted health plan`,
      });
      queryClient.invalidateQueries({
        queryKey: clientClassificationsQueryKeys.index(),
      });

      queryClient.resetQueries({
        queryKey: classificationsQueryKeys.index({
          'client.not': clientId,
        }),
      });

      handleClose();
    },
    onError: () => {
      addToast({
        text: `Something went wrong. Please try again.`,
      });
    },
  });

  const handleRemove = () => {
    deletePlan();
  };

  return (
    <StandardModal title='Remove Health Plan?' onCancel={handleClose} colorTheme={ColorThemeName.PrimaryRed}>
      <BoldBodySmall>Are you sure you wish to remove {healthPlan.name} as a contracted health plan?</BoldBodySmall>

      <BodySmall>Health plans can be added back as a contracted health plan at any time.</BodySmall>
      <StyledButtonGroup>
        <Button variant='ghost' size='small' onClick={handleClose}>
          Cancel
        </Button>
        <Button variant='danger' size='small' onClick={handleRemove}>
          Remove Health Plan
        </Button>
      </StyledButtonGroup>
    </StandardModal>
  );
};

const BoldBodySmall = styled(BodySmall)`
  font-weight: bold;
  margin-bottom: 24px;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 32px;
`;

export default RemoveConfirmModal;
